import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Home, 
  ZoomIn, 
  ZoomOut, 
  Maximize, 
  Search,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { codesPracticeDocuments, getPdfUrl } from '../data/pdfDocuments';
import PVSPageLayout from './PVSPageLayout';
import { DEFAULT_PDF_ZOOM } from '../utils/pdfViewerDefaults';

// Configure PDF.js worker - using local worker file for v3.x
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const PDFViewer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { docId } = useParams();
  
  // URL state management
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialZoom = parseFloat(searchParams.get('zoom')) || DEFAULT_PDF_ZOOM;
  const initialSearch = searchParams.get('q') || '';
  
  // Core state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(initialZoom);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [pageTextCache, setPageTextCache] = useState(new Map());
  
  // Refs
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const highlightLayerRef = useRef(null);
  const activeSearchTermRef = useRef('');
  
  // Find document
  const pdfDocument = codesPracticeDocuments.find(doc => doc.id === docId);
  
  // Update URL with current state
  const updateURL = useCallback((page, zoomLevel, query) => {
    const params = new URLSearchParams();
    if (page !== 1) params.set('page', page.toString());
    if (zoomLevel !== DEFAULT_PDF_ZOOM) params.set('zoom', zoomLevel.toFixed(2));
    if (query) params.set('q', query);
    
    const newSearch = params.toString();
    const newPath = `/viewer/${docId}${newSearch ? `?${newSearch}` : ''}`;
    
    if (location.pathname + location.search !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [docId, navigate, location]);
  
  // Load PDF document
  useEffect(() => {
    if (!pdfDocument) {
      setError('Document not found');
      setIsLoading(false);
      return;
    }
    
    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const pdfUrl = getPdfUrl(pdfDocument.filename);
        console.log('Loading PDF from URL:', pdfUrl);
        
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setIsLoading(false);
        
        // If there's an initial search query, perform search
        if (initialSearch) {
          setSearchQuery(initialSearch);
        }
      } catch (err) {
        console.error('Error loading PDF:', err);
        console.error('PDF URL that failed:', getPdfUrl(pdfDocument.filename));
        setError(`Failed to load PDF document: ${err.message}`);
        setIsLoading(false);
      }
    };
    
    loadPDF();
  }, [pdfDocument, initialSearch]);
  
  // Highlight search results on current page
  const highlightSearchResults = useCallback(async (pageNum, viewport, searchTerm) => {
    if (!highlightLayerRef.current || !searchTerm || searchResults.length === 0) {
      if (highlightLayerRef.current) {
        highlightLayerRef.current.innerHTML = '';
      }
      return;
    }
    
    highlightLayerRef.current.style.width = `${viewport.width}px`;
    highlightLayerRef.current.style.height = `${viewport.height}px`;
    
    highlightLayerRef.current.innerHTML = '';
    
    const pageResults = searchResults.filter(result => result.page === pageNum);
    
    if (pageResults.length === 0) {
      return;
    }
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      let fullText = '';
      const charPositions = [];
      
      textContent.items.forEach(item => {
        fullText += item.str;
        
        for (let i = 0; i < item.str.length; i++) {
          charPositions.push({
            char: item.str[i],
            x: item.transform[4] + (i * item.width / item.str.length),
            y: item.transform[5],
            width: item.width / item.str.length,
            height: item.height,
            transform: item.transform
          });
        }
        fullText += ' ';
        charPositions.push({ char: ' ', x: 0, y: 0, width: 0, height: 0 });
      });
      
      const lowerSearchTerm = searchTerm.toLowerCase();
      const lowerText = fullText.toLowerCase();
      
      let index = 0;
      while ((index = lowerText.indexOf(lowerSearchTerm, index)) !== -1) {
        if (index < charPositions.length && index + lowerSearchTerm.length <= charPositions.length) {
          const firstChar = charPositions[index];
          const lastCharIndex = index + lowerSearchTerm.length - 1;
          
          if (lastCharIndex < charPositions.length) {
            const lastChar = charPositions[lastCharIndex];
            
            const highlight = document.createElement('div');
            highlight.style.position = 'absolute';
            highlight.style.left = `${firstChar.x}px`;
            highlight.style.top = `${viewport.height - firstChar.y - firstChar.height}px`;
            highlight.style.width = `${(lastChar.x + lastChar.width) - firstChar.x}px`;
            highlight.style.height = `${firstChar.height}px`;
            highlight.style.backgroundColor = 'rgba(0, 255, 0, 0.3)';
            highlight.style.pointerEvents = 'none';
            highlight.style.borderRadius = '2px';
            
            const isCurrentMatch = searchResults[currentSearchIndex]?.page === pageNum && 
                                   searchResults[currentSearchIndex]?.index === index;
            if (isCurrentMatch) {
              highlight.style.backgroundColor = 'rgba(255, 215, 0, 0.5)';
              highlight.style.border = '2px solid #FFD700';
            }
            
            highlightLayerRef.current.appendChild(highlight);
          }
        }
        
        index += lowerSearchTerm.length;
      }
      
    } catch (err) {
      console.error('Error highlighting search results:', err);
    }
  }, [pdfDoc, searchResults, currentSearchIndex]);
  
  // Render current page
  const renderPage = useCallback(async (pageNum, zoomLevel) => {
    console.log('🔄 renderPage called for page', pageNum);
    if (!pdfDoc || !canvasRef.current) return;
    
    if (renderTaskRef.current) {
      console.log('⚠️ Cancelling previous render task');
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      
      const pageRotation = page.rotate || 0;
      
      const viewport = page.getViewport({ scale: zoomLevel, rotation: pageRotation });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      renderTaskRef.current = page.render(renderContext);
      
      await renderTaskRef.current.promise;
      
      renderTaskRef.current = null;
      
      highlightSearchResults(pageNum, viewport, activeSearchTermRef.current);
      
      if (textLayerRef.current) {
        textLayerRef.current.innerHTML = '';
        textLayerRef.current.style.width = `${viewport.width}px`;
        textLayerRef.current.style.height = `${viewport.height}px`;
        
        const textContent = await page.getTextContent();
        
        textContent.items.forEach(item => {
          const div = document.createElement('div');
          div.textContent = item.str;
          div.style.position = 'absolute';
          div.style.left = `${item.transform[4]}px`;
          div.style.top = `${viewport.height - item.transform[5] - item.height}px`;
          div.style.fontSize = `${item.height}px`;
          div.style.fontFamily = item.fontName || 'sans-serif';
          div.style.color = 'transparent';
          div.style.userSelect = 'text';
          div.style.pointerEvents = 'auto';
          
          textLayerRef.current.appendChild(div);
        });
      }
      
    } catch (err) {
      renderTaskRef.current = null;
      
      if (err.name === 'RenderingCancelledException') {
        console.log('⚠️ Render cancelled (expected during navigation)');
        return;
      }
      
      console.error('❌ Error rendering page:', err);
    }
  }, [pdfDoc, highlightSearchResults]);
  
  // Update page and URL when page or zoom changes
  useEffect(() => {
    if (pdfDoc && currentPage >= 1 && currentPage <= totalPages) {
      renderPage(currentPage, zoom);
    }
    
    return () => {
      if (renderTaskRef.current) {
        console.log('🧹 Cleanup: Cancelling render task');
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, zoom, totalPages, renderPage]);
  
  useEffect(() => {
    if (searchResults.length > 0 && activeSearchTermRef.current) {
      updateURL(currentPage, zoom, activeSearchTermRef.current);
    }
  }, [searchResults, currentPage, zoom, updateURL]);
  
  useEffect(() => {
    if (pdfDoc && currentPage && searchResults.length > 0 && highlightLayerRef.current) {
      const canvas = canvasRef.current;
      if (canvas) {
        pdfDoc.getPage(currentPage).then(page => {
          const pageRotation = page.rotate || 0;
          const viewport = page.getViewport({ scale: zoom, rotation: pageRotation });
          highlightSearchResults(currentPage, viewport, activeSearchTermRef.current);
        }).catch(err => {
          console.error('Error re-highlighting:', err);
        });
      }
    }
  }, [currentSearchIndex, pdfDoc, currentPage, zoom, searchResults, highlightSearchResults]);
  
  // Extract text from page (cached)
  const getPageText = useCallback(async (pageNum) => {
    if (!pdfDoc) return '';
    
    if (pageTextCache.has(pageNum)) {
      return pageTextCache.get(pageNum);
    }
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      const text = textContent.items.map(item => item.str).join(' ');
      
      setPageTextCache(prev => new Map(prev).set(pageNum, text));
      return text;
    } catch (err) {
      console.error('Error extracting text from page:', err);
      return '';
    }
  }, [pdfDoc, pageTextCache]);
  
  // Search functionality
  const performSearch = useCallback(async (query) => {
    if (!query.trim() || !pdfDoc) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }
    
    setIsSearching(true);
    const results = [];
    const searchTerm = query.toLowerCase();
    
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const pageText = await getPageText(pageNum);
      const lowerPageText = pageText.toLowerCase();
      
      let index = 0;
      while ((index = lowerPageText.indexOf(searchTerm, index)) !== -1) {
        results.push({
          page: pageNum,
          index: index,
          text: pageText.substring(Math.max(0, index - 20), index + searchTerm.length + 20)
        });
        index += searchTerm.length;
      }
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    setIsSearching(false);
    
    activeSearchTermRef.current = query;
    
    if (results.length > 0) {
      setCurrentPage(results[0].page);
    }
  }, [pdfDoc, totalPages, getPageText]);
  
  // Search handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    console.log('⌨️ Search query changed to:', query);
    setSearchQuery(query);
  };
  
  const handleSearchSubmit = () => {
    performSearch(searchQuery);
  };
  
  const handleSearchNext = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    setCurrentPage(searchResults[nextIndex].page);
  }, [searchResults, currentSearchIndex]);
  
  const handleSearchPrev = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    setCurrentPage(searchResults[prevIndex].page);
  }, [searchResults, currentSearchIndex]);
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
    activeSearchTermRef.current = '';
    updateURL(currentPage, zoom, '');
  };
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      
      if (document.activeElement === searchInputRef.current) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (e.shiftKey) {
            handleSearchPrev();
          } else {
            handleSearchNext();
          }
        }
        return;
      }
      
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          setCurrentPage(prev => Math.max(1, prev - 1));
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          setCurrentPage(prev => Math.min(totalPages, prev + 1));
          break;
        case '=':
        case '+':
          e.preventDefault();
          setZoom(prev => Math.min(3.0, prev + 0.25));
          break;
        case '-':
          e.preventDefault();
          setZoom(prev => Math.max(0.5, prev - 0.25));
          break;
        default:
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [totalPages, handleSearchNext, handleSearchPrev]);
  
  // Zoom handlers
  const handleZoomIn = () => setZoom(prev => Math.min(3.0, prev + 0.25));
  const handleZoomOut = () => setZoom(prev => Math.max(0.5, prev - 0.25));
  const handleFitToWidth = () => {
    if (containerRef.current && canvasRef.current) {
      const containerWidth = containerRef.current.clientWidth - 40;
      const canvasWidth = canvasRef.current.width / zoom;
      const newZoom = containerWidth / canvasWidth;
      setZoom(Math.min(3.0, Math.max(0.5, newZoom)));
    }
  };
  
  // Navigation handlers
  const handleBack = () => {
    navigate('/home', { state: { view: 'codes-of-practice' } });
  };
  const handleHome = () => navigate('/home');
  const handlePageChange = (e) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  if (!pdfDocument) {
    return (
      <PVSPageLayout className="flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Document Not Found</h2>
          <button
            onClick={handleHome}
            className="flex items-center gap-2 px-4 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200"
          >
            <Home className="w-4 h-4" />
            Return Home
          </button>
        </div>
      </PVSPageLayout>
    );
  }
  
  return (
    <PVSPageLayout className="flex flex-col">
      {/* Toolbar */}
      <div className="pvs-glass border-b border-pvs-border shadow-pvs-glow px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <button
              onClick={handleHome}
              className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
              title="Home"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </button>
          </div>
          
          {/* Document title */}
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-white truncate">
              {pdfDocument.title}
            </h1>
          </div>
          
          {/* Right controls */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-pvs-text-dim" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchSubmit();
                      }
                    }}
                    placeholder="Search in document..."
                    className="pl-9 pr-8 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim w-48"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-pvs-text-dim hover:text-pvs-text-muted"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <button
                  onClick={handleSearchSubmit}
                  disabled={!searchQuery.trim()}
                  className="px-3 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                  title="Search (Enter)"
                >
                  Search
                </button>
                
                {searchResults.length > 0 && (
                  <div className="flex items-center gap-1 ml-2">
                    <span className="text-sm text-pvs-text-muted">
                      {currentSearchIndex + 1} of {searchResults.length}
                    </span>
                    <button
                      onClick={handleSearchPrev}
                      className="p-1 text-pvs-text-muted hover:text-white rounded"
                      title="Previous match (Shift+Enter)"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSearchNext}
                      className="p-1 text-pvs-text-muted hover:text-white rounded"
                      title="Next match (Enter)"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {isSearching && (
                  <div className="ml-2 text-sm text-pvs-text-muted">Searching...</div>
                )}
              </div>
            </div>
            
            {/* Zoom controls */}
            <div className="flex items-center gap-1 border-l border-pvs-border pl-2">
              <button
                onClick={handleZoomOut}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Zoom Out (-)"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              
              <span className="text-sm text-pvs-text-muted min-w-[4rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              
              <button
                onClick={handleZoomIn}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Zoom In (+)"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              
              <button
                onClick={handleFitToWidth}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Fit to Width"
              >
                <Maximize className="w-4 h-4" />
              </button>
            </div>
            
            {/* Page controls */}
            {totalPages > 0 && (
              <div className="flex items-center gap-2 border-l border-pvs-border pl-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage <= 1}
                  className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                  title="Previous Page (← or ↑)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <span className="text-sm text-pvs-text-muted">Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={handlePageChange}
                  className="w-16 px-2 py-1 bg-pvs-input-bg border border-pvs-input-border text-white rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50"
                />
                <span className="text-sm text-pvs-text-muted">of {totalPages}</span>
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                  title="Next Page (→ or ↓)"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 overflow-auto bg-pvs-bg" ref={containerRef}>
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pvs-accent mx-auto mb-4"></div>
              <p className="text-pvs-text-muted">Loading PDF...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={handleHome}
                className="flex items-center gap-2 px-4 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200"
              >
                <Home className="w-4 h-4" />
                Return Home
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-8 gap-6">
            {/* PDF Display */}
            <div className="relative bg-white shadow-lg" style={{ zIndex: 1 }}>
              <canvas
                ref={canvasRef}
                className="block"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
              <div
                ref={textLayerRef}
                className="absolute top-0 left-0 overflow-hidden leading-none"
                style={{ mixBlendMode: 'multiply', pointerEvents: 'none' }}
              />
              <div
                ref={highlightLayerRef}
                className="absolute top-0 left-0 overflow-hidden leading-none pointer-events-none"
              />
            </div>
            
            {/* Large Navigation Buttons */}
            <div 
              className="flex items-center gap-4 pvs-glass border border-pvs-border px-6 py-4 rounded-lg shadow-pvs-glow"
              style={{ 
                position: 'relative', 
                zIndex: 10,
                marginTop: '20px'
              }}
            >
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="flex items-center gap-2 px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ 
                  touchAction: 'manipulation', 
                  minHeight: '44px', 
                  minWidth: '44px',
                  position: 'relative',
                  zIndex: 11
                }}
                title="Previous Page"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Previous</span>
              </button>
              
              <div className="text-center px-4">
                <div className="text-lg font-semibold text-white">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
              
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="flex items-center gap-2 px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ 
                  touchAction: 'manipulation', 
                  minHeight: '44px', 
                  minWidth: '44px',
                  position: 'relative',
                  zIndex: 11
                }}
                title="Next Page"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </PVSPageLayout>
  );
};

export default PDFViewer;
